import React, { useState, useMemo, useEffect } from 'react';
import { useMutation, useApolloClient } from '@apollo/client';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { SAVE_REPORT_DATA, GET_REPORTS, GET_REPORT } from '../../graphql/reports';
import EmployeeSummary from './EmployeeSummary';
import ReportJobFlags from './ReportJobFlags';
import { generateServiceReportName } from '../../utils/reportNaming';

const ServiceReportView = ({ reportData, reportType, roleFilter, startDate, endDate, savedReportId, savedReportName = null, onBackToSelection, appliedFilters = {}, onReportSaved = null }) => {
  const client = useApolloClient();
  const [sortField, setSortField] = useState('scheduledDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Create a deep copy of reportData to avoid read-only issues from Apollo cache
  const createEditableData = (data) => {
    return JSON.parse(JSON.stringify(data));
  };
  
  const [editableData, setEditableData] = useState(() => createEditableData(reportData));
  
  // Auto-save state
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // Update editableData when reportData changes (e.g., when loading a saved report)
  useEffect(() => {
    setEditableData(createEditableData(reportData));
    // Reset unsaved changes when new data is loaded
    setHasUnsavedChanges(false);
  }, [reportData]);

  // Add before unload handler to save and warn about unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && savedReportId) {
        // Try to save before leaving
        handleAutoSave('before-unload');
        
        // Browser warning
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, savedReportId]);

  const [saveReportData] = useMutation(SAVE_REPORT_DATA, {
    refetchQueries: [{ query: GET_REPORTS }],
    onCompleted: (data) => {
      // Don't show success message or exit edit mode for auto-saves
      // Manual saves will be handled in handleSaveReport
      setHasUnsavedChanges(false); // Reset unsaved changes after successful save
      
              // If we're updating an existing report, update the Apollo cache for GET_REPORT
        if (savedReportId && data.saveReportData) {
          // Update the cache for the specific report
          try {
            client.cache.modify({
              id: client.cache.identify({ __typename: 'Report', id: savedReportId }),
              fields: {
                reportDataJson: () => data.saveReportData.reportDataJson,
                totalPayout: () => data.saveReportData.totalPayout,
                totalCost: () => data.saveReportData.totalCost,
                totalExpenses: () => data.saveReportData.totalExpenses,
                finalTotal: () => data.saveReportData.finalTotal,
                jobsCount: () => data.saveReportData.jobsCount
              }
            });
          } catch (error) {
            // Silently handle cache update errors
          }
        }
        
        // Notify parent component that report was saved
        if (onReportSaved && data.saveReportData) {
          onReportSaved(data.saveReportData);
        }
    },
    onError: (error) => {
      console.error('Error saving report:', error);
      toast.error('Failed to save report. Please try again.');
    }
  });

  // Flatten all jobs from all team members into a single array
  const allJobs = useMemo(() => {
    const jobs = [];
    
    // Add safety check for teamMembers
    if (editableData.teamMembers && Array.isArray(editableData.teamMembers)) {
      editableData.teamMembers.forEach(teamMember => {
        if (teamMember.jobs && Array.isArray(teamMember.jobs)) {
          teamMember.jobs.forEach(jobData => {
            jobs.push({
              ...jobData,
              teamMemberName: teamMember.teamMemberName,
              teamMemberId: teamMember.teamMemberId,
              teamMemberRole: teamMember.teamMemberRole
            });
          });
        }
      });
    }

    // Sort jobs
    return jobs.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'property':
          aValue = a.job.property.name;
          bValue = b.job.property.name;
          break;
        case 'teamMember':
          aValue = a.teamMemberName || '';
          bValue = b.teamMemberName || '';
          break;
        case 'serviceType':
          aValue = a.job.serviceType?.title || '';
          bValue = b.job.serviceType?.title || '';
          break;
        case 'area':
          aValue = a.job.property.area || '';
          bValue = b.job.property.area || '';
          break;
        case 'status':
          aValue = a.job.status || '';
          bValue = b.job.status || '';
          break;
        case 'scheduledDate':
          aValue = new Date(a.job.scheduledDate);
          bValue = new Date(b.job.scheduledDate);
          break;
        case 'completedDate':
          aValue = a.job.completedAt ? new Date(a.job.completedAt) : new Date(0);
          bValue = b.job.completedAt ? new Date(b.job.completedAt) : new Date(0);
          break;
        case 'payout':
          aValue = a.calculatedPayout || 0;
          bValue = b.calculatedPayout || 0;
          break;
        case 'billedAmount':
          aValue = a.calculatedBilledAmount || 0;
          bValue = b.calculatedBilledAmount || 0;
          break;
        default:
          aValue = a.job.scheduledDate;
          bValue = b.job.scheduledDate;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [editableData, sortField, sortDirection]);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    let date;
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Handle YYYY-MM-DD strings by adding time component to avoid UTC interpretation
      date = new Date(dateString + 'T00:00:00');
    } else if (typeof dateString === 'number') {
      // Handle timestamp values - extract UTC date and create local date
      const tempDate = new Date(dateString);
      const year = tempDate.getUTCFullYear();
      const month = tempDate.getUTCMonth();
      const day = tempDate.getUTCDate();
      date = new Date(year, month, day); // Create as local date
    } else {
      date = new Date(dateString);
    }
    
    return format(date, 'MMM dd, yyyy');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      IN_PROGRESS: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'In Progress' },
      COMPLETED: { bg: 'bg-green-100', text: 'text-green-800', label: 'Completed' },
      CANCELLED: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled' },
      FAILED: { bg: 'bg-red-200', text: 'text-red-900', label: 'Failed' },
      NEEDS_ATTENTION: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Needs Attention' },
      READY_FOR_INSPECTION: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Ready for Inspection' }
    };

    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 ml-1 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 ml-1 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  // Calculate totals - only include completed jobs for payout totals
  const totalPayout = allJobs.reduce((sum, job) => {
    return sum + (job.job.status === 'COMPLETED' ? (job.calculatedPayout || 0) : 0);
  }, 0);
  const totalBilledAmount = allJobs.reduce((sum, job) => {
    return sum + (job.job.status === 'COMPLETED' ? (job.calculatedBilledAmount || 0) : 0);
  }, 0);

  const handleFieldEdit = (jobId, field, value) => {
    setEditableData(prevData => {
      const newData = { ...prevData };
      
      // Add safety check for teamMembers
      if (!newData.teamMembers || !Array.isArray(newData.teamMembers)) {
        return newData;
      }
      
      // Find the team member and job that contains this jobId
      for (let teamMemberIndex = 0; teamMemberIndex < newData.teamMembers.length; teamMemberIndex++) {
        const teamMember = newData.teamMembers[teamMemberIndex];
        if (!teamMember.jobs || !Array.isArray(teamMember.jobs)) {
          continue;
        }
        
        const jobIndex = teamMember.jobs.findIndex(jobData => jobData.job.id === jobId);
        
        if (jobIndex !== -1) {
          const updatedTeamMember = { ...teamMember };
          const updatedJob = { ...updatedTeamMember.jobs[jobIndex] };
          
          if (field === 'notes') {
            updatedJob.job = { ...updatedJob.job, notes: value };
          } else if (field === 'payout') {
            const numericValue = parseFloat(value) || 0;
            updatedJob.calculatedPayout = numericValue;
            updatedJob.isPayoutOverridden = true;
            updatedJob.customPayout = numericValue;
          } else if (field === 'billedAmount') {
            const numericValue = parseFloat(value) || 0;
            updatedJob.calculatedBilledAmount = numericValue;
            updatedJob.isBilledAmountOverridden = true;
            updatedJob.customBilledAmount = numericValue;
          }
          
          updatedTeamMember.jobs[jobIndex] = updatedJob;
          
          // Recalculate team member totals if payout or billed amount changed
          if (field === 'payout' || field === 'billedAmount') {
            updatedTeamMember.totalPayout = updatedTeamMember.jobs.reduce((sum, job) => sum + (job.calculatedPayout || 0), 0);
            updatedTeamMember.totalBilledAmount = updatedTeamMember.jobs.reduce((sum, job) => sum + (job.calculatedBilledAmount || 0), 0);
          }
          
          newData.teamMembers[teamMemberIndex] = updatedTeamMember;
          
          // Recalculate grand totals if payout or billed amount changed
          if (field === 'payout' || field === 'billedAmount') {
            newData.grandTotalPayout = newData.teamMembers.reduce((sum, tm) => sum + (tm.totalPayout || 0), 0);
            newData.grandTotalBilledAmount = newData.teamMembers.reduce((sum, tm) => sum + (tm.totalBilledAmount || 0), 0);
            newData.finalTotal = newData.grandTotalPayout + (newData.totalExpenses || 0);
          }
          
          break;
        }
      }
      
      return newData;
    });
    
    // Mark as having unsaved changes
    setHasUnsavedChanges(true);
  };

  const handleBackToSelection = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'Are you sure you want to leave? You have unsaved changes that will be lost.'
      );
      if (!confirmed) {
        return;
      }
    }
    onBackToSelection();
  };

  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'Are you sure you want to cancel? Your changes will be lost.'
      );
      if (!confirmed) {
        return;
      }
    }
    setIsEditing(false);
    setHasUnsavedChanges(false);
    // Reset data to original state
    setEditableData(createEditableData(reportData));
  };

  // Function to generate descriptive report name based on applied filters
  const generateReportName = () => {
    // Use saved report name if it exists (preserve custom names), otherwise generate default name
    if (savedReportName) {
      return savedReportName;
    }
    
    // Extract areas from appliedFilters for consistent naming
    const selectedAreas = appliedFilters?.areas || [];
    
    // Use centralized utility for consistent naming
    return generateServiceReportName(selectedAreas);
  };

  const handleSaveReport = async () => {
    setIsSaving(true);
    try {
      const reportName = generateReportName();
      
      // Clean up GraphQL __typename fields before saving
      const cleanData = (obj) => {
        if (obj === null || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(cleanData);
        
        const cleaned = {};
        for (const [key, value] of Object.entries(obj)) {
          if (key !== '__typename') {
            cleaned[key] = cleanData(value);
          }
        }
        return cleaned;
      };
      
      const cleanedData = cleanData(editableData);
      
      await saveReportData({
        variables: {
          input: {
            name: reportName,
            type: reportType,
            startDate: startDate,
            endDate: endDate,
            roleFilter: roleFilter,
            reportDataJson: JSON.stringify(cleanedData),
            totalPayout: cleanedData.grandTotalPayout,
            totalCost: cleanedData.grandTotalBilledAmount,
            totalExpenses: cleanedData.totalExpenses || 0,
            finalTotal: cleanedData.finalTotal || cleanedData.grandTotalPayout,
            jobsCount: cleanedData.totalJobs,
            existingReportId: savedReportId || null
          }
        }
      });
      
      // Manual save success: show message and exit edit mode
      const message = savedReportId ? 'Report updated successfully!' : 'Report saved successfully!';
      toast.success(message);
      setIsEditing(false);
      
    } catch (error) {
      // Error handling is done in the mutation's onError callback
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Auto-save function that saves report without user interaction
   * Triggered by before-unload event
   */
  const handleAutoSave = async (trigger = 'manual') => {
    // Don't auto-save if:
    // - Already saving
    // - No saved report ID (can't update non-existent report)
    // - No unsaved changes
    if (isSaving || !savedReportId || !hasUnsavedChanges) {
      return;
    }
    
    setIsSaving(true);
    try {
      const reportName = generateReportName();
      
      // Clean up GraphQL __typename fields before saving
      const cleanData = (obj) => {
        if (obj === null || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(cleanData);
        
        const cleaned = {};
        for (const [key, value] of Object.entries(obj)) {
          if (key !== '__typename') {
            cleaned[key] = cleanData(value);
          }
        }
        return cleaned;
      };
      
      const cleanedData = cleanData(editableData);
      
      await saveReportData({
        variables: {
          input: {
            name: reportName,
            type: reportType,
            startDate: startDate,
            endDate: endDate,
            roleFilter: roleFilter,
            reportDataJson: JSON.stringify(cleanedData),
            totalPayout: cleanedData.grandTotalPayout,
            totalCost: cleanedData.grandTotalBilledAmount,
            totalExpenses: cleanedData.totalExpenses || 0,
            finalTotal: cleanedData.finalTotal || cleanedData.grandTotalPayout,
            jobsCount: cleanedData.totalJobs,
            existingReportId: savedReportId
          }
        }
      });
      
      // Update last saved timestamp
      setLastSavedAt(new Date());
      
    } catch (error) {
      console.error('Auto-save failed:', error);
      toast.error('Auto-save failed. Your changes are still safe locally.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Service Based Report
          </h1>
          <p className="text-gray-600">
            {format(new Date(startDate + 'T00:00:00'), 'MMM dd, yyyy')} - {format(new Date(endDate + 'T00:00:00'), 'MMM dd, yyyy')}
          </p>
          {appliedFilters?.areas && appliedFilters.areas.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Filtered by areas:</span>
              {appliedFilters.areas.map((area) => (
                <span key={area} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-800">
                  {area.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
              ))}
            </div>
          )}
        </div>
        
        {/* Top Action Buttons */}
        <div className="flex items-center space-x-3">
          <button
            onClick={handleBackToSelection}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sky-500"
          >
            ← Back to Reports
          </button>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Edit Report
            </button>
          ) : (
            <>
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sky-500"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveReport}
                disabled={isSaving}
                className="px-6 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save Report'
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Jobs Table - Moved to top */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">All Jobs</h3>
          <p className="text-sm text-gray-500">{allJobs.length} jobs found</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-[230px]"
                  onClick={() => handleSort('property')}
                >
                  <div className="flex items-center">
                    Property
                    {getSortIcon('property')}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('teamMember')}
                >
                  <div className="flex items-center">
                    Assigned Team
                    {getSortIcon('teamMember')}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 max-w-[175px]"
                  onClick={() => handleSort('serviceType')}
                >
                  <div className="flex items-center">
                    Service Type
                    {getSortIcon('serviceType')}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('area')}
                >
                  <div className="flex items-center">
                    Area
                    {getSortIcon('area')}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center">
                    Status
                    {getSortIcon('status')}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('scheduledDate')}
                >
                  <div className="flex items-center">
                    Date
                    {getSortIcon('scheduledDate')}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('completedDate')}
                >
                  <div className="flex items-center">
                    Date Completed
                    {getSortIcon('completedDate')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px]">
                  Notes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[150px]">
                  Flags
                </th>
                <th 
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('payout')}
                >
                  <div className="flex items-center justify-end">
                    Payout
                    {getSortIcon('payout')}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('billedAmount')}
                >
                  <div className="flex items-center justify-end">
                    Billed Amount
                    {getSortIcon('billedAmount')}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {allJobs.map((jobData, index) => (
                <tr key={jobData.job.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        <td className="px-6 py-4 text-sm text-gray-900 w-[230px]">
                        <div>
                          <div className="font-medium break-words">{jobData.job.property.name}</div>
                          <div className="text-gray-500 break-words">{jobData.job.property.propertyCode}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {jobData.teamMemberName || (
                      <span className="text-gray-400 italic">Unassigned</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-[175px]">
                    <div className="truncate">
                      {jobData.job.serviceType?.title || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {jobData.job.property.area || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getStatusBadge(jobData.job.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(jobData.job.scheduledDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(jobData.job.completedAt)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 min-w-[180px]">
                    {isEditing ? (
                      <textarea
                        value={jobData.job.notes || ''}
                        onChange={(e) => handleFieldEdit(jobData.job.id, 'notes', e.target.value)}
                        placeholder="Add notes..."
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
                        rows="2"
                      />
                    ) : (
                      <div className="whitespace-pre-wrap break-words">
                        {jobData.job.notes || (
                          <span className="text-gray-400 italic">No notes</span>
                        )}
                      </div>
                    )}
                  </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-[150px]">
                    <ReportJobFlags 
                      isEarlyCheckin={jobData.job.isEarlyCheckin}
                      isLateCheckout={jobData.job.isLateCheckout}
                      isOwnerBlock={jobData.job.isOwnerBlock}
                      isMaintenanceBlock={jobData.job.isMaintenanceBlock}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    <div className="flex items-center justify-end">
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          value={jobData.calculatedPayout || 0}
                          onChange={(e) => handleFieldEdit(jobData.job.id, 'payout', e.target.value)}
                          className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-sky-500 text-right"
                          disabled={jobData.job.status !== 'COMPLETED'}
                        />
                      ) : (
                        <span className={jobData.job.status !== 'COMPLETED' ? 'text-gray-400' : ''}>
                          {formatCurrency(jobData.job.status === 'COMPLETED' ? jobData.calculatedPayout : 0)}
                        </span>
                      )}
                      {jobData.isPayoutOverridden && jobData.job.status === 'COMPLETED' && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                          Override
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    <div className="flex items-center justify-end">
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          value={jobData.calculatedBilledAmount || 0}
                          onChange={(e) => handleFieldEdit(jobData.job.id, 'billedAmount', e.target.value)}
                          className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-sky-500 text-right"
                          disabled={jobData.job.status !== 'COMPLETED'}
                        />
                      ) : (
                        <span className={jobData.job.status !== 'COMPLETED' ? 'text-gray-400' : ''}>
                          {formatCurrency(jobData.job.status === 'COMPLETED' ? jobData.calculatedBilledAmount : 0)}
                        </span>
                      )}
                      {jobData.isBilledAmountOverridden && jobData.job.status === 'COMPLETED' && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                          Override
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom Action Bar for Jobs Table */}
      <div className="bg-white border border-gray-200 rounded-lg shadow">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6 text-sm text-gray-600">
              <span>
                <strong>{allJobs.length}</strong> jobs
              </span>
              <span>•</span>
              <span>
                <strong>{formatCurrency(totalPayout)}</strong> payout
              </span>
              <span>•</span>
              <span>
                <strong>{formatCurrency(totalBilledAmount)}</strong> billed
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Employee Summary */}
      <EmployeeSummary 
        teamMembers={editableData.teamMembers || []} 
        reportType={reportType} 
      />

      {/* Summary Card */}
      <div className="bg-white rounded-lg shadow max-w-md mx-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 text-center">Report Summary</h3>
        </div>
        <div className="px-6 py-4">
          {(() => {
            // Calculate employee and contractor labor costs
            const employees = editableData.teamMembers?.filter(member => 
              member.teamMemberId !== 0 && member.isContractor !== true
            ) || [];
            
            const contractors = editableData.teamMembers?.filter(member => 
              member.teamMemberId !== 0 && member.isContractor === true
            ) || [];
            
            const employeeLabor = employees.reduce((sum, member) => sum + (Number(member.totalPayout) || 0), 0);
            const contractorLabor = contractors.reduce((sum, member) => sum + (Number(member.totalPayout) || 0), 0);
            const totalExpensesAmount = editableData.totalExpenses || 0;
            const netProfit = totalBilledAmount - employeeLabor - contractorLabor - totalExpensesAmount;
            const profitPercentage = totalBilledAmount > 0 ? (netProfit / totalBilledAmount * 100) : 0;

            return (
              <dl className="space-y-4">
                <div className="flex justify-between text-sm">
                  <dt className="text-gray-600 font-medium">Total Billed:</dt>
                  <dd className="font-semibold text-gray-900">{formatCurrency(totalBilledAmount)}</dd>
                </div>
                
                <div className="flex justify-between text-sm">
                  <dt className="text-gray-600 font-medium">Employee Labor:</dt>
                  <dd className="font-semibold text-gray-900">{formatCurrency(employeeLabor)}</dd>
                </div>
                
                <div className="flex justify-between text-sm">
                  <dt className="text-gray-600 font-medium">Contractor Labor:</dt>
                  <dd className="font-semibold text-gray-900">{formatCurrency(contractorLabor)}</dd>
                </div>
                
                <div className="flex justify-between text-sm">
                  <dt className="text-gray-600 font-medium">Expenses:</dt>
                  <dd className="font-semibold text-gray-900">{formatCurrency(totalExpensesAmount)}</dd>
                </div>
                
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <dt className="text-base font-semibold text-gray-900">Net Profit:</dt>
                    <dd className="text-right">
                      <div className="text-base font-bold text-gray-900">
                        {formatCurrency(netProfit)}
                      </div>
                      <div className={`text-sm font-medium ${profitPercentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {profitPercentage >= 0 ? '+' : ''}{profitPercentage.toFixed(1)}%
                      </div>
                    </dd>
                  </div>
                </div>
              </dl>
            );
          })()}
        </div>
      </div>


    </div>
  );
};

export default ServiceReportView; 